/**
 * Supabase error_logs writer for pricing-critical service paths.
 *
 * Per PLAN_WORKFLOW_METHODOLOGY.md §5.5, services in src/services/ wrap
 * exceptions so /work completion reports can query the last hour of
 * error_logs before asking the user to ship. Without this writer, the
 * table stays empty and the safety layer is incomplete.
 *
 * Contract: this function MUST NEVER throw and MUST NEVER block the caller.
 * Logging failures degrade silently to a console.debug — pricing flow is
 * never disrupted by a logging issue.
 *
 * Authorization: the underlying RPC is SECURITY DEFINER and validates that
 * the caller is a member of `teamId`. error_logs has no INSERT policy for
 * authenticated, so direct inserts 403 — the RPC is the only path.
 */
import { supabase } from '@/supabaseClient';
import { logger } from './logger';

export type ErrorLogLevel = 'error' | 'warn' | 'info' | 'debug';

/** Cap for the message column. Far above any real exception message. */
const MAX_MESSAGE_LENGTH = 4000;

/**
 * Append a row to public.error_logs for the given team.
 *
 * Always logs to the local logger first (so dev console shows the error
 * even if the network write fails or teamId is missing). Then attempts the
 * Supabase RPC. Any failure is swallowed.
 *
 * @param teamId        Caller's current team. If undefined (e.g. logged-out
 *                      path or team not yet selected), only the local logger
 *                      runs — no Supabase write is attempted.
 * @param message       Human-readable summary of the exception. Truncated to
 *                      4000 chars.
 * @param context       Structured metadata (operation, file name, ids, etc.).
 *                      Stored as JSONB. May be omitted.
 * @param level         Severity. Defaults to 'error'.
 */
export async function logErrorToSupabase(
  teamId: string | undefined,
  message: string,
  context?: Record<string, unknown>,
  level: ErrorLogLevel = 'error'
): Promise<void> {
  // Always echo to the local logger first so the developer sees it
  // regardless of whether the Supabase write succeeds.
  if (level === 'warn') {
    logger.warn(`[errorLog] ${message}`, context);
  } else if (level === 'info') {
    logger.info(`[errorLog] ${message}`, context);
  } else if (level === 'debug') {
    logger.debug(`[errorLog] ${message}`, context);
  } else {
    logger.error(`[errorLog] ${message}`, context);
  }

  if (!teamId) {
    logger.debug('[errorLog] Supabase write skipped — no teamId in scope');
    return;
  }

  const trimmedMessage =
    message.length > MAX_MESSAGE_LENGTH
      ? message.slice(0, MAX_MESSAGE_LENGTH)
      : message;

  try {
    const { error } = await supabase.rpc('log_error_to_team', {
      p_team_id: teamId,
      p_message: trimmedMessage,
      p_context: (context ?? null) as unknown as object | null,
      p_level: level,
    });
    if (error) {
      logger.debug('[errorLog] Supabase RPC returned error:', error.message);
    }
  } catch (err) {
    // Logging must never break the caller. Swallow.
    logger.debug('[errorLog] Supabase RPC threw:', err);
  }
}

/**
 * Convenience: build a context object from an Error plus extras.
 * Captures message + stack trace head, leaving room for caller-supplied keys.
 */
export function buildErrorContext(
  error: unknown,
  extras?: Record<string, unknown>
): Record<string, unknown> {
  const base: Record<string, unknown> = { ...(extras ?? {}) };

  if (error instanceof Error) {
    base.errorName = error.name;
    base.errorMessage = error.message;
    if (error.stack) {
      // Keep the first 4 frames — full stacks bloat JSONB without much value.
      base.stackHead = error.stack.split('\n').slice(0, 5).join('\n');
    }
  } else if (typeof error === 'string') {
    base.errorMessage = error;
  } else if (error && typeof error === 'object') {
    try {
      base.errorRaw = JSON.parse(JSON.stringify(error));
    } catch {
      base.errorRaw = String(error);
    }
  }

  return base;
}
